import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { FormControl, FormGroup, Validators } from '@angular/forms';

import { EMPTY, Subscription, switchMap, take, tap } from 'rxjs';
import { MessageService } from 'primeng/api';

import { CANDIDATE_FORM_UI } from '../../constants/ui-strings';
import { TEST_CONST, TOAST_CONFIGURATION } from 'src/app/constants/constants';
import { StateManagementService } from 'src/app/services/state-management.service';
import { TestService } from 'src/app/services/test.service';
import { StartTestData } from 'src/app/model/start-test-data';
import { QuestionsService } from 'src/app/services/questions.service';
import { QuestionResponse } from 'src/app/model/question';


@Component({
  selector: 'app-candidate-form',
  templateUrl: './candidate-form.component.html',
  styleUrl: './candidate-form.component.scss',
})
export class CandidateFormComponent implements OnInit, OnDestroy {
  @Input() disableButton: boolean;
  isLoading: boolean = false;
  candidateFormUIString = CANDIDATE_FORM_UI;
  candidateDetailsForm!: FormGroup;
  candidateInfo: StartTestData;
  showInstructions: boolean = false;
  hasReadInstructions: boolean = false
  instructionImages: string[] = TEST_CONST.instruction_images;
  currentIndex: number = 0;
  startTestSubscription$: Subscription;
  isScroll: boolean = false;
  checkBoxMsg: boolean = false;

  constructor (
      private router: Router,
      private messageService: MessageService,
      private stateManagementService: StateManagementService,
      private testService: TestService,
      private questionsService: QuestionsService
  ) {}

  ngOnInit(): void {
    this.candidateInfo = this.testService.testData();
    this.candidateDetailsForm = new FormGroup({
      name: new FormControl({ value: this.candidateInfo.test_name, disabled: true}, [Validators.required]),
      email: new FormControl({ value: this.candidateInfo.email, disabled: true }, [Validators.required, Validators.email]),
      password: new FormControl('', [Validators.required])
    });
  }

  ngOnDestroy(): void {
    this.startTestSubscription$?.unsubscribe();
  }

  openInstruction(): void {
    if(this.candidateDetailsForm.invalid) {
      return;
    }
    if (!this.isScroll) {
      this.checkBoxMsg = true;
      return;
    }
    this.showInstructions = true;
  }

  startTestHelper(): void {
    this.stateManagementService.isTestRouteAllowed = true;
    this.router.navigateByUrl('/test');
  }

  clearInputs(): void {
    this.candidateDetailsForm.reset();
  }

  async enableCamera(): Promise<boolean> {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: true });
      stream.getTracks().forEach(track => track.stop());
      return true;
    } catch (error) {
      this.messageService.add({
        severity: TOAST_CONFIGURATION.warning,
        detail: CANDIDATE_FORM_UI.camera_permission,
      });
      return false;
    }
  }

  nextImage(): void {
    if (this.currentIndex < this.instructionImages.length - 1) {
      this.currentIndex++;
    }
  }

  prevImage(): void {
    if (this.currentIndex > 0) {
      this.currentIndex--;
    }
  }

  async startTest(): Promise<void> {
    if (this.hasReadInstructions) {
      this.showInstructions = false;
    }
    if (this.candidateInfo?.proctor_settings?.enforce_camera === true) {
      const isCameraPermitted = await this.enableCamera();
      if (!isCameraPermitted) {
        return;
      }
    }
    await this.testService.goFullscreen(this.candidateInfo?.proctor_settings?.enforce_fullscreen).catch(() => {
      // Error already handled in service
    });

    this.isLoading = true;
    this.startTestSubscription$ = this.testService
    .startTest(this.candidateDetailsForm.get('password').value)
    .pipe(
      take(1),
      switchMap((response) => {
        if(response.success) {
          sessionStorage.setItem(TEST_CONST.auth_token,response.body.token);
          const test_id = this.testService.testData().test_id
          const candidate_id = this.testService.testData().email
          return this.questionsService.getQuestionList(test_id, candidate_id)
        } else {
          this.messageService.add({
            severity: 'info',
            detail: response.message
          })
          this.isLoading = false;
          return EMPTY;
        }
      })
    )
    .subscribe({
      next: (response: QuestionResponse) => {
        if (response.success) {
          this.questionsService.questionsList = response.body;
          this.questionsService.totalQuestions = response.body.length;
          this.questionsService.questionRecords = response.body.map((question) => {
            const record = {
              isMarkedForReview: false,
              isVisited: false,
              isAnswered: question.CANDIDATE_ANSWER.length > 0,
              selectedOption: question.CANDIDATE_ANSWER,
              isLast: false
            };
            if (record.isAnswered) {
              this.questionsService.actionQuestion(true);
            }
            return record;
          })
        }
        this.messageService.add({
          severity: response.success ? 
            TOAST_CONFIGURATION.success : TOAST_CONFIGURATION.warning,
          detail: response.message,
          closable: true
        });
        this.stateManagementService.isTestRouteAllowed = true;
        this.isLoading = false;
        this.router.navigateByUrl('/test');
      },
      error: (error) => {
        this.messageService.add({
          severity: TOAST_CONFIGURATION.failed,
          detail: error.message,
          closable: true
        });
        this.isLoading = false;
      }
    });
  }

  isScrolled(event: boolean): void {
    this.isScroll = event
  }
}
